import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useValidateLogin } from '../../hooks/auth';
import { DASHBOARD_PATH_PREFIXES } from '../../utils/constants';
import Storage from '../../utils/Storage';

const GET_STARTED_PATH = '/app/get_started';
const CTEM_DASHBOARD_PATH = '/app/ctem_dashboard';
const REDIRECTED_FLAG = 'get_started_redirected';

/**
 * Single top-level mount point (mirrors PendingCompanyGate) that redirects an onboarding-incomplete
 * company to the Get Started screen once per browser session (on login/first dashboard landing) —
 * the full sidebar stays visible and navigable per product decision, so this must not keep
 * bouncing a user back every time they deliberately navigate elsewhere (including a checklist CTA
 * that first navigates to another dashboard route before opening a modal there). A sessionStorage
 * flag (survives a hard refresh, unlike a component-lifetime ref) makes that one-shot durable —
 * set the FIRST time onboarding_completed resolves on a dashboard route this session, whether or
 * not a redirect actually fired (e.g. the user's first dashboard landing this session was already
 * /app/get_started itself), so every later navigation this session is left alone. Deliberately
 * NOT set on a non-dashboard route (e.g. /payment, /profile) even if onboarding_completed
 * resolves there first (useValidateLogin's query is shared app-wide) — otherwise the flag gets
 * marked "checked" before the user ever reaches a dashboard page, and the redirect below never
 * gets a chance to fire. Redirecting away from Get Started to the
 * CTEM dashboard once onboarding completes is NOT one-shot — that check runs on every render so it
 * fires the moment onboarding flips to complete. `onboarding_completed` is a strict boolean (null
 * only for provider/mssp/super_admin accounts, which have no single company in context and so
 * never gate on this at all).
 */
const GetStartedGate = () => {
  const { data } = useValidateLogin();
  const { pathname } = useLocation();

  const isOnDashboard = DASHBOARD_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const onboardingCompleted = data?.success ? data?.user?.onboarding_completed : undefined;
  // PendingCompanyGate already owns the redirect for a company that hasn't paid — defer to it
  // instead of racing it for a company that is both payment-pending and onboarding-incomplete.
  const isCompanyPending = data?.success && data?.user?.company_status === 'pending';
  const hasCheckedThisSession = Storage.getData(REDIRECTED_FLAG, true) === 'true';

  useEffect(() => {
    // Gated on isOnDashboard — this component is mounted globally (every route, including
    // /payment and /profile), and useValidateLogin's query is shared app-wide, so
    // onboardingCompleted can resolve while the user is still on Payment/Profile, well before
    // their first real dashboard landing. Without this guard the flag gets marked "checked" on
    // one of those pages and the redirect below never gets a chance to fire at all.
    if (isOnDashboard && onboardingCompleted !== undefined && !hasCheckedThisSession) {
      Storage.setData(REDIRECTED_FLAG, 'true', true);
    }
  }, [isOnDashboard, onboardingCompleted, hasCheckedThisSession]);

  if (
    onboardingCompleted === false &&
    isOnDashboard &&
    pathname !== GET_STARTED_PATH &&
    !isCompanyPending &&
    !hasCheckedThisSession
  ) {
    return <Navigate to={GET_STARTED_PATH} replace />;
  }

  if (onboardingCompleted === true && pathname === GET_STARTED_PATH) {
    return <Navigate to={CTEM_DASHBOARD_PATH} replace />;
  }

  return null;
};

export default GetStartedGate;
